import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Download, FileText, Save, Settings2 } from 'lucide-react';
import api from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import ReportEditor from './ReportEditor';
import SprsEditor from './SprsEditor';
import { AVAILABLE_TYPES, REPORT_BLURBS, REPORT_DEFS } from './reportSchemas';

const SIGNATORY_ROLES = [
  ['reported_by', 'Reported by', 'LMI Focal'],
  ['certified_by', 'Certified by', 'PESO Manager'],
  ['prepared_by', 'Prepared by', 'PESO Manager'],
  ['noted_by', 'Noted by', 'Municipal Mayor'],
];

// The month picker defaults to the current Manila month, not the staffer's device month.
const manilaMonth = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Manila' }).slice(0, 7);

const relTime = (iso) => {
  if (!iso) return '';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString();
};

/**
 * DOLE monthly report forms: pick a month, open a form, edit it, export to Word/Excel.
 * Mounted inside the PESO dashboard's Reports tab alongside the SPRS summary cards.
 */
export default function PesoReportsForms({ authHeaders, officeName }) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState(manilaMonth);
  const [openType, setOpenType] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await api.get('/api/peso/reports/forms-status', { headers, params: { period } });
      setDrafts(res.data.drafts || {});
    } catch (e) {
      // Non-blocking: the cards simply show "Not started" if this fails.
      setDrafts({});
    }
  }, [authHeaders, period]);

  const loadSettings = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await api.get('/api/peso/reports/settings', { headers });
      setSettings(res.data.report_settings || {});
    } catch (e) {
      setSettings({});
    }
  }, [authHeaders]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  const setSig = (role, key, value) => {
    setSettings((s) => ({
      ...s,
      signatories: {
        ...(s?.signatories || {}),
        [role]: { ...((s?.signatories || {})[role] || {}), [key]: value },
      },
    }));
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const headers = await authHeaders();
      const res = await api.put('/api/peso/reports/settings', { report_settings: settings }, { headers });
      setSettings(res.data.report_settings);
      toast.success(t('peso.settingsSaved', 'Report settings saved'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('peso.settingsSaveFailed', 'Failed to save settings'));
    } finally {
      setSavingSettings(false);
    }
  };

  // Download every form for the month as Word, one after another. No zip in v1 —
  // browsers throttle rapid programmatic downloads, so space them out.
  const downloadAll = async () => {
    setDownloadingAll(true);
    try {
      const headers = await authHeaders();
      for (const type of AVAILABLE_TYPES) {
        try {
          const res = await api.get(`/api/peso/reports/forms/${type}/export`, {
            headers, params: { period, format: 'docx' }, responseType: 'blob', timeout: 60000,
          });
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const a = document.createElement('a');
          a.href = url;
          a.download = `PESO_${type}_${period}.docx`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        } catch (e) {
          toast.error(t('peso.downloadOneFailed', { type, defaultValue: `Could not download ${type}` }));
        }
        await new Promise((r) => setTimeout(r, 700));
      }
      toast.success(t('peso.downloadAllDone', 'All forms downloaded'));
    } finally {
      setDownloadingAll(false);
    }
  };

  if (openType) {
    const Editor = REPORT_DEFS[openType].custom === 'sprs' ? SprsEditor : ReportEditor;
    return (
      <Editor
        reportType={openType}
        period={period}
        authHeaders={authHeaders}
        settings={settings}
        officeName={officeName}
        onBack={() => { setOpenType(null); loadStatus(); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="h-4 w-4" /> {t('peso.doleFormsTitle', 'DOLE Monthly Forms')}
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              {t('peso.doleFormsBody', 'Pre-filled from your office data. Edit anything, add rows for walk-ins, then download for submission.')}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs text-muted-foreground" htmlFor="peso-report-month">
              {t('peso.month', 'Month')}
            </label>
            <Input id="peso-report-month" type="month" value={period}
              onChange={(e) => setPeriod(e.target.value)} className="h-9 w-[160px]" />
            <Button variant="outline" size="sm" onClick={downloadAll} disabled={downloadingAll} className="h-9">
              <Download size={14} className="mr-1" />
              {downloadingAll ? t('peso.downloadingAll', 'Downloading…') : t('peso.downloadAll', 'Download all (Word)')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {AVAILABLE_TYPES.map((type) => {
              const draft = drafts[type];
              return (
                <button key={type} type="button" onClick={() => setOpenType(type)}
                  className="text-left rounded-lg border border-slate-200 bg-white p-3 hover:border-blue-400 hover:shadow-sm transition">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold text-sm text-slate-900">{REPORT_DEFS[type].title}</div>
                    {draft
                      ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 shrink-0">{t('peso.draftSaved', 'Draft saved')}</Badge>
                      : <Badge variant="outline" className="shrink-0">{t('peso.notStarted', 'Not started')}</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{REPORT_BLURBS[type]}</p>
                  {draft && (
                    <p className="text-[11px] text-slate-500 mt-2">
                      {draft.updated_by_name ? `${draft.updated_by_name} · ` : ''}{relTime(draft.updated_at)}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <button type="button" onClick={() => setShowSettings((v) => !v)}
            className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Settings2 className="h-4 w-4" />
            {t('peso.reportSettingsTitle', 'Signatories & header')}
            <span className="text-xs font-normal text-muted-foreground">
              {showSettings ? t('peso.hide', 'hide') : t('peso.show', 'show')}
            </span>
          </button>
        </CardHeader>
        {showSettings && (
          <CardContent className="space-y-4">
            <p className="text-xs text-muted-foreground">
              {t('peso.reportSettingsBody', 'Saved once for your office and printed on every exported form.')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {SIGNATORY_ROLES.map(([role, label, titleHint]) => (
                <div key={role} className="rounded-lg border border-slate-200 p-3 space-y-2">
                  <div className="text-xs font-semibold text-slate-700">{label}</div>
                  <Input placeholder={t('peso.fullName', 'Full name')}
                    value={settings?.signatories?.[role]?.name || ''}
                    onChange={(e) => setSig(role, 'name', e.target.value)} className="h-9" />
                  <Input placeholder={titleHint}
                    value={settings?.signatories?.[role]?.title || ''}
                    onChange={(e) => setSig(role, 'title', e.target.value)} className="h-9" />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <div className="text-xs font-semibold text-slate-700 mb-1">{t('peso.doleRegion', 'DOLE Region')}</div>
                <Input value={settings?.dole_region || ''} placeholder="VII"
                  onChange={(e) => setSettings((s) => ({ ...s, dole_region: e.target.value }))} className="h-9" />
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-700 mb-1">{t('peso.fieldOffice', 'Field Office')}</div>
                <Input value={settings?.field_office || ''} placeholder="BOHOL FIELD OFFICE"
                  onChange={(e) => setSettings((s) => ({ ...s, field_office: e.target.value }))} className="h-9" />
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-700 mb-1">{t('peso.lmiCertNo', 'LMI Certificate No.')}</div>
                <Input value={settings?.lmi_certificate_number || ''} placeholder="AJA15-0048"
                  onChange={(e) => setSettings((s) => ({ ...s, lmi_certificate_number: e.target.value }))} className="h-9" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button size="sm" onClick={saveSettings} disabled={savingSettings}>
                <Save size={14} className="mr-1" />
                {savingSettings ? t('peso.saving', 'Saving…') : t('peso.save', 'Save')}
              </Button>
              <span className="text-xs text-muted-foreground">
                {t('peso.draftRetention', 'Report drafts are kept for 24 months, then automatically deleted.')}
              </span>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
